/**
 * Página de mis peticiones del usuario autenticado
 */
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Button,
    useTheme,
    useMediaQuery,
    Box,
    Card,
    Typography,
    Divider
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";

import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { StateContext } from "../../../components/context/StateProvider";
import PeticionesService from "../api/peticiones.api";
import UsuariosService from "../../../services/usuarios.service";
import Toasts from "../../../components/notificacion/Toasts";
import { PETICION_ESTADO, PETICION_TIPO } from "../constants";
import { generateMisPeticionesColumns } from "../helpers/columns";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { ModalEliminar, MisPeticionesMobileList, FiltrosPeticiones, BADGE_COLORS } from "../components";

// Aspecto común del DataGrid más el color de fondo según el tipo de petición
const SX_GRID = {
    ...DATA_GRID_SX,
    "& .row-tipo-peticion": { backgroundColor: "rgba(255, 243, 224, 0.15)" },
    "& .row-tipo-reposicion": { backgroundColor: "rgba(227, 242, 253, 0.15)" },
};

// Valores iniciales de fechas para los filtros
const getFechaInicioInicial = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const getFechaFinInicial = () => new Date().toISOString().slice(0, 10);

const MisPeticiones = () => {
    const { state, updateState, getNombreAlmacen } = useContext(StateContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [peticiones, setPeticiones] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    // processingId guarda el id de la peticion cuya accion de eliminacion esta en curso,
    // impidiendo dobles clicks y aportando feedback visual en esa fila.
    const [processingId, setProcessingId] = useState(null);

    // Estados de filtros
    const [fechaInicio, setFechaInicio] = useState(getFechaInicioInicial);
    const [fechaFin, setFechaFin] = useState(getFechaFinInicial);
    const [empleado, setEmpleado] = useState('');
    const [tipo, setTipo] = useState('');
    const [q, setQ] = useState('');
    // Filtros aplicados (separados de inputs para aplicar solo al hacer click)
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        fechaInicio: getFechaInicioInicial(),
        fechaFin: getFechaFinInicial(),
        estado: '',
        empleado: '',
        tipo: '',
        q: ''
    });

    // Modal de eliminación
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [peticionToDelete, setPeticionToDelete] = useState(null);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [datos, emp] = await Promise.all([
                PeticionesService.listMias(),
                UsuariosService.listarUsuarios()
            ]);
            setPeticiones(datos);
            setEmpleados(emp ?? []);
            setLoading(false);
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
            setLoading(false);
        }
    };

    const handleNuevaPeticion = () => {
        navigate('/mis-peticiones/nueva-peticion');
    };

    const handleEliminarPeticion = (peticionId) => {
        const peticion = peticiones.find(p => p.id === peticionId);
        if (!peticion) return;

        setPeticionToDelete(peticion);
        setShowModalEliminar(true);
    };

    const cerrarModalEliminar = () => {
        setShowModalEliminar(false);
        setPeticionToDelete(null);
    };

    const confirmarEliminacion = async () => {
        if (!peticionToDelete) return;

        setProcessingId(peticionToDelete.id);
        try {
            await PeticionesService.delete(peticionToDelete.id);
            const toast = Toasts.generateToast('Petición eliminada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
            cerrarModalEliminar();
            await cargarDatos();
        } catch (error) {
            const mensajeError = error.response?.data?.error?.message || error.message;
            const toast = Toasts.generateToast(mensajeError, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            // Se resetea tanto en exito como en error para desbloquear siempre la fila.
            setProcessingId(null);
        }
    };

    const handleFiltrar = () => {
        setFiltrosAplicados(prev => ({
            ...prev,
            fechaInicio,
            fechaFin,
            empleado,
            tipo,
            q
        }));
    };

    const handleLimpiar = () => {
        setFechaInicio(getFechaInicioInicial());
        setFechaFin(getFechaFinInicial());
        setEmpleado('');
        setTipo('');
        setQ('');
        setFiltrosAplicados({
            fechaInicio: getFechaInicioInicial(),
            fechaFin: getFechaFinInicial(),
            estado: '',
            empleado: '',
            tipo: '',
            q: ''
        });
    };

    const handleEstadisticaClick = (estadoValor) => {
        // Si ya está activo ese filtro, lo quita
        if (filtrosAplicados.estado === estadoValor) {
            setFiltrosAplicados(prev => ({ ...prev, estado: '' }));
        } else {
            // Aplica el nuevo filtro
            setFiltrosAplicados(prev => ({ ...prev, estado: estadoValor }));
        }
    };

    // Peticiones filtradas
    const peticionesFiltradas = useMemo(() => {
        let filtradas = [...peticiones];

        // Filtro por fechas
        if (filtrosAplicados.fechaInicio) {
            const fechaInicioFiltro = new Date(filtrosAplicados.fechaInicio);
            fechaInicioFiltro.setHours(0, 0, 0, 0);
            filtradas = filtradas.filter(p => {
                const fechaPeticion = p.creado ? new Date(p.creado) : null;
                return fechaPeticion && fechaPeticion >= fechaInicioFiltro;
            });
        }

        if (filtrosAplicados.fechaFin) {
            const fechaFinFiltro = new Date(filtrosAplicados.fechaFin);
            fechaFinFiltro.setHours(23, 59, 59, 999);
            filtradas = filtradas.filter(p => {
                const fechaPeticion = p.creado ? new Date(p.creado) : null;
                return fechaPeticion && fechaPeticion <= fechaFinFiltro;
            });
        }

        // Filtro por estado
        if (filtrosAplicados.estado) {
            filtradas = filtradas.filter(p => p.estado === filtrosAplicados.estado);
        }

        // Filtro por empleado (reponedor asignado)
        if (filtrosAplicados.empleado) {
            filtradas = filtradas.filter(p =>
                p.reponedor && String(p.reponedor.id) === String(filtrosAplicados.empleado)
            );
        }

        // Filtro por tipo
        if (filtrosAplicados.tipo) {
            filtradas = filtradas.filter(p => (p.tipo || PETICION_TIPO.PETICION.value) === filtrosAplicados.tipo);
        }

        // Filtro por búsqueda texto
        if (filtrosAplicados.q) {
            const searchLower = filtrosAplicados.q.toLowerCase();
            filtradas = filtradas.filter(p => {
                return (
                    p.nombreProducto?.toLowerCase().includes(searchLower) ||
                    p.referencia?.toLowerCase().includes(searchLower) ||
                    p.numeroAlbaran?.toLowerCase().includes(searchLower) ||
                    p.usuario?.username?.toLowerCase().includes(searchLower) ||
                    p.reponedor?.username?.toLowerCase().includes(searchLower)
                );
            });
        }

        return filtradas;
    }, [peticiones, filtrosAplicados]);

    // Estadísticas de peticiones (sobre datos filtrados)
    const estadisticas = useMemo(() => {
        const noAtendidas = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.NO_ATENDIDO.value).length;
        const enProceso = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.EN_PROCESO.value).length;
        const preparadas = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.PREPARADO.value).length;

        return { noAtendidas, enProceso, preparadas, total: peticionesFiltradas.length };
    }, [peticionesFiltradas]);

    const columns = generateMisPeticionesColumns({ getNombreAlmacen, processingId, onEliminar: handleEliminarPeticion });

    return (
        <Box id="mis-peticiones" sx={{ width: "100%" }}>
            <Card
                sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                }}
            >
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <RequestPageIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Mis peticiones
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Gestiona tus solicitudes de productos
                            </Typography>
                        </Box>
                        {!isMobile && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleNuevaPeticion}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                Nueva petición
                            </Button>
                        )}
                    </Box>
                    {isMobile && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNuevaPeticion}
                            fullWidth
                            sx={{ ...BOTON_PRIMARIO_SX, mb: 2 }}
                        >
                            Nueva petición
                        </Button>
                    )}

                    {/* Estadísticas */}
                    {!loading && peticiones.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, 1fr)",
                                        sm: "repeat(4, 1fr)",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                {/* No atendidas */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.NO_ATENDIDO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.RED.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.NO_ATENDIDO.value ? `2px solid ${BADGE_COLORS.RED.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <WarningAmberIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.RED.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            No atendidas
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.RED.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.noAtendidas}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* En proceso */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.EN_PROCESO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.AMBER.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.EN_PROCESO.value ? `2px solid ${BADGE_COLORS.AMBER.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <HourglassEmptyIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.AMBER.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            En proceso
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.AMBER.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.enProceso}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Preparadas */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.PREPARADO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.GREEN.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.PREPARADO.value ? `2px solid ${BADGE_COLORS.GREEN.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.GREEN.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Preparadas
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.GREEN.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.preparadas}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Total */}
                                <Box
                                    onClick={() => handleEstadisticaClick('')}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.BLUE.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: !filtrosAplicados.estado ? `2px solid ${BADGE_COLORS.BLUE.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <InventoryIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.BLUE.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: BADGE_COLORS.BLUE.text,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Total
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.BLUE.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Filtros */}
                    {!loading && peticiones.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <FiltrosPeticiones
                                fechaInicio={fechaInicio}
                                setFechaInicio={setFechaInicio}
                                fechaFin={fechaFin}
                                setFechaFin={setFechaFin}
                                empleado={empleado}
                                setEmpleado={setEmpleado}
                                tipo={tipo}
                                setTipo={setTipo}
                                q={q}
                                setQ={setQ}
                                empleadosOptions={empleados}
                                onFiltrar={handleFiltrar}
                                onLimpiar={handleLimpiar}
                                loading={loading}
                                totalResultados={peticionesFiltradas.length}
                                fechaInicioInicial={getFechaInicioInicial()}
                                fechaFinInicial={getFechaFinInicial()}
                            />
                        </>
                    )}
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {!isMobile ? (
                        <DataGrid
                            rows={peticionesFiltradas}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            getRowClassName={(params) => {
                                const tipo = params.row.tipo || PETICION_TIPO.PETICION.value;
                                return tipo === PETICION_TIPO.REPOSICION.value ? 'row-tipo-reposicion' : 'row-tipo-peticion';
                            }}
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            disableRowSelectionOnClick
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={SX_GRID}
                        />
                    ) : (
                        <MisPeticionesMobileList
                            rows={peticionesFiltradas}
                            loading={loading}
                            onEliminar={handleEliminarPeticion}
                            processingId={processingId}
                            getNombreAlmacen={getNombreAlmacen}
                        />
                    )}
                </Box>
            </Card>

            {/* Modal de confirmación de eliminación */}
            <ModalEliminar
                show={showModalEliminar}
                onHide={cerrarModalEliminar}
                peticion={peticionToDelete}
                onConfirm={confirmarEliminacion}
                loading={processingId !== null}
            />
        </Box>
    );
};

export default MisPeticiones;
